import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Certification,
  CertificationProperty,
  CertHouseholdData,
  CertMember,
  HouseholdMember,
} from 'src/types/Certification';
import { MANAGING_UHM_ROLES } from 'src/types/Certification';
import type { UserHouseholdData } from 'src/types/Certification';
import type { CertStatusMap } from 'src/types/CertStatus';
import { FormSignatureRequestType } from 'src/types/SignatureTypes';
import type { QuestionnaireSchema } from 'src/components/WSCertificationSummary/types/QuestionnaireSchemaTypes';
import type {
  QuestionnaireAnswer,
  QuestionnaireAnswerFile,
  QuestionnaireAnswersMap,
} from 'src/services/RenterQuestionnaire.service';

export interface UnconfirmedHouseholdWithProperty {
  household: CertHouseholdData;
  certificationId: number;
  property?: CertificationProperty;
}

export type TransferredEventData = {
  new_head?: HouseholdMember;
  demoted_member: HouseholdMember;
  household_id: number;
  certification_id: number;
};

interface UpdateHouseholdMemberPayload {
  certificationId: number;
  householdMember: CertMember;
  action: 'add' | 'update' | 'remove';
}

interface UpdateHouseholdNamePayload {
  certificationId: number;
  name: string;
}

interface UpsertAnswerPayload {
  certificationId: number;
  answer: QuestionnaireAnswer;
}

interface RemoveAnswerPayload {
  certificationId: number;
  qitId: number;
  surveyId: number;
  answerGroupId: number;
}

interface AddFileToAnswerPayload {
  certificationId: number;
  qitId: number;
  surveyId: number;
  answerGroupId: number;
  file: QuestionnaireAnswerFile;
}

interface RemoveFileFromAnswerPayload {
  certificationId: number;
  qitId: number;
  surveyId: number;
  answerGroupId: number;
  fileId: number;
}

interface SurveyEventPayload {
  certificationId: number;
  survey: {
    id: number;
    certification_id: number;
    cert_member_id: number | null;
  };
}

interface NewRenterInterfaceState {
  highlights: number[];
  fsrHighlights: number[];
  certifications: Certification[];
  certificationsLoaded: boolean;
  fsrsLoaded: boolean;
  certificationIds: number[];
  certStatus: CertStatusMap; // cert_id → { status_text, action_items } (IVR facts + text)
  certStatusLoading: boolean;
  unconfirmedHouseholds: UnconfirmedHouseholdWithProperty[];
  householdsTotalCount: number;
  householdsCurrentIndex: number;
  householdsConfirmed: number[];
  householdsTransferred: TransferredEventData[];
  openHouseholdComposition: boolean;
  openHouseholdCompositionForCertId: number | null;
  schemas: Record<number, QuestionnaireSchema>;
  schemasLoading: Record<number, boolean>;
  schemasFailed: Record<number, boolean>;
  answers: Record<number, QuestionnaireAnswersMap>;
  answersLoading: Record<number, boolean>;
  answersLoaded: Record<number, boolean>;
  answersFailed: Record<number, boolean>;
  questionnaireVisibility: Record<
    number,
    {
      visibleGroupIds: number[];
      visibleQitIds: number[];
      newlyRevealed: number[];
      revealOrigin: Record<
        number,
        { questionName: string; choiceName: string }
      >;
      filteredQitIds: number[];
      memberTypeId: number | null;
    }
  >;
  userHouseholds: UserHouseholdData[];
  userHouseholdsLoaded: boolean;
  /**
   * UserHouseholds whose profile composition still needs confirming. Only
   * populated for cert-less (profile-only) renters — see
   * computeUnconfirmedProfileHouseholds. Cert-bound households are tracked on
   * the CertHousehold, not here.
   */
  unconfirmedProfileHouseholds: UserHouseholdData[];
}

const initialState: NewRenterInterfaceState = {
  highlights: [],
  fsrHighlights: [],
  certifications: [],
  certificationsLoaded: false,
  fsrsLoaded: false,
  certificationIds: [],
  certStatus: {},
  certStatusLoading: false,
  unconfirmedHouseholds: [],
  householdsTotalCount: 0,
  householdsCurrentIndex: 0,
  householdsConfirmed: [],
  householdsTransferred: [],
  openHouseholdComposition: false,
  openHouseholdCompositionForCertId: null,
  schemas: {},
  schemasLoading: {},
  schemasFailed: {},
  answers: {},
  answersLoading: {},
  answersLoaded: {},
  answersFailed: {},
  questionnaireVisibility: {},
  userHouseholds: [],
  userHouseholdsLoaded: false,
  unconfirmedProfileHouseholds: [],
};

const computeUnconfirmedHouseholds = (
  certifications: Certification[],
  userId: number
): UnconfirmedHouseholdWithProperty[] => {
  if (!certifications?.length || !userId) {
    return [];
  }

  return certifications
    .filter((cert) => {
      const ch = cert.cert_household;
      if (!ch || ch.composition_confirmed) {
        return false;
      }
      if (cert.is_done === true) {
        return false;
      }
      const memberForUser = ch.cert_members.find(
        (m: CertMember) => m.user_id === userId
      );
      return memberForUser?.is_managing === true;
    })
    .map((cert) => ({
      household: cert.cert_household!,
      certificationId: cert.id,
      property: cert.property,
    })) as UnconfirmedHouseholdWithProperty[];
};

/**
 * Profile (cert-less) households still awaiting composition confirmation.
 * A UserHousehold counts as unconfirmed when the current user holds a
 * managing role in it and its `composition_confirmed` flag is falsy.
 *
 * Cert-bound households are deliberately NOT filtered out here — the
 * resolution to "the profile household" happens in
 * `resolveProfileHousehold`, which excludes any UH already backed by a
 * certification so the cert composition modal owns that case.
 */
const computeUnconfirmedProfileHouseholds = (
  userHouseholds: UserHouseholdData[]
): UserHouseholdData[] => {
  if (!userHouseholds?.length) {
    return [];
  }
  return userHouseholds.filter(
    (uh) => MANAGING_UHM_ROLES.includes(uh.my_role) && !uh.composition_confirmed
  );
};

const NewRenterInterfaceSlice = createSlice({
  name: 'newRenterInterface',
  initialState,
  reducers: {
    addHighlight: (state, action: PayloadAction<number>) => {
      if (!state.highlights.includes(action.payload)) {
        state.highlights.push(action.payload);
      }
    },
    removeHighlight: (state, action: PayloadAction<number>) => {
      state.highlights = state.highlights.filter((id) => id !== action.payload);
    },
    clearHighlights: (state) => {
      state.highlights = [];
    },
    addFsrHighlight: (state, action: PayloadAction<number>) => {
      if (!state.fsrHighlights.includes(action.payload)) {
        state.fsrHighlights.push(action.payload);
      }
    },
    removeFsrHighlight: (state, action: PayloadAction<number>) => {
      state.fsrHighlights = state.fsrHighlights.filter(
        (id) => id !== action.payload
      );
    },
    setCertifications: (state, action: PayloadAction<Certification[]>) => {
      state.certifications = action.payload ?? [];
      state.certificationIds = (action.payload ?? []).map((cert) => cert.id);
      state.certificationsLoaded = true;
    },
    setUserHouseholds: (state, action: PayloadAction<UserHouseholdData[]>) => {
      state.userHouseholds = action.payload ?? [];
      state.userHouseholdsLoaded = true;
      // Recompute the derived profile-unconfirmed list on every household
      // fetch (JWTContext + Account Hub refetch dispatch this action).
      state.unconfirmedProfileHouseholds = computeUnconfirmedProfileHouseholds(
        state.userHouseholds
      );
    },
    clearCertifications: (state) => {
      state.certifications = [];
      state.certificationIds = [];
      state.certificationsLoaded = false;
      state.fsrsLoaded = false;
      state.unconfirmedHouseholds = [];
      state.householdsTotalCount = 0;
      state.householdsCurrentIndex = 0;
      state.householdsConfirmed = [];
      state.householdsTransferred = [];
      state.openHouseholdComposition = false;
      state.openHouseholdCompositionForCertId = null;
      state.schemas = {};
      state.schemasLoading = {};
      state.schemasFailed = {};
      state.answers = {};
      state.answersLoading = {};
      state.answersLoaded = {};
      state.answersFailed = {};
      state.questionnaireVisibility = {};
      state.userHouseholds = [];
      state.userHouseholdsLoaded = false;
      state.unconfirmedProfileHouseholds = [];
    },
    setCertStatus: (state, action: PayloadAction<Record<string, unknown>>) => {
      const raw = action.payload ?? {};
      // Merge into existing entries rather than replacing, so a partial
      // (single-cert) fetch doesn't drop entries fetched by another surface.
      const converted: CertStatusMap = { ...state.certStatus };
      for (const certIdStr of Object.keys(raw)) {
        converted[Number(certIdStr)] = raw[certIdStr] as CertStatusMap[number];
      }
      state.certStatus = converted;
    },
    setCertStatusLoading: (state, action: PayloadAction<boolean>) => {
      state.certStatusLoading = action.payload;
    },
    setOpenHouseholdComposition: (state, action: PayloadAction<boolean>) => {
      state.openHouseholdComposition = action.payload;
    },
    setOpenHouseholdCompositionForCertId: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.openHouseholdCompositionForCertId = action.payload;
      // Also set the modal open flag when opening for a specific cert
      if (action.payload !== null) {
        state.openHouseholdComposition = true;
      }
    },
    setUnconfirmedHouseholds: (state, action: PayloadAction<number>) => {
      state.unconfirmedHouseholds = computeUnconfirmedHouseholds(
        state.certifications,
        action.payload
      );
      if (state.householdsTotalCount === 0) {
        state.householdsTotalCount = state.unconfirmedHouseholds.length;
      }
    },
    incrementHouseholdsCurrentIndex: (state) => {
      state.householdsCurrentIndex += 1;
    },
    setHouseholdsCurrentIndex: (state, action: PayloadAction<number>) => {
      state.householdsCurrentIndex = action.payload;
    },
    updateHouseholdMemberInCertifications: (
      state,
      action: PayloadAction<UpdateHouseholdMemberPayload>
    ) => {
      const {
        certificationId,
        householdMember,
        action: actionType,
      } = action.payload;
      const cert = state.certifications.find((c) => c.id === certificationId);
      if (!cert?.cert_household) {
        return;
      }

      const members = cert.cert_household.cert_members || [];

      if (actionType === 'add') {
        const alreadyPresent = members.some(
          (m) => m.cert_member_id === householdMember.cert_member_id
        );
        if (!alreadyPresent) {
          const sorted = [...members, householdMember].sort(
            (a, b) =>
              (a.member_type_weight ?? 999) - (b.member_type_weight ?? 999)
          );
          cert.cert_household.cert_members = sorted;
        }
      } else if (actionType === 'update') {
        const index = members.findIndex(
          (m) => m.cert_member_id === householdMember.cert_member_id
        );
        if (index !== -1) {
          const next = [...members];
          next[index] = { ...next[index], ...householdMember };
          cert.cert_household.cert_members = next.sort(
            (a, b) =>
              (a.member_type_weight ?? 999) - (b.member_type_weight ?? 999)
          );
        }
      } else if (actionType === 'remove') {
        cert.cert_household.cert_members = members.filter(
          (m) => m.cert_member_id !== householdMember.cert_member_id
        );
      }
    },
    updateCertMemberContactsByUserId: (
      state,
      action: PayloadAction<{
        userId: number;
        emails: CertMember['emails'];
        phones: CertMember['phones'];
      }>
    ) => {
      const { userId, emails, phones } = action.payload;
      for (const cert of state.certifications) {
        const household = cert.cert_household;
        if (!household?.cert_members) {
          continue;
        }
        household.cert_members = household.cert_members.map((m) =>
          m.user_id === userId ? { ...m, emails, phones } : m
        );
      }
    },
    setHouseholdCompositionConfirmed: (
      state,
      action: PayloadAction<{ certificationId: number; userId: number }>
    ) => {
      const { certificationId, userId } = action.payload;
      const cert = state.certifications.find((c) => c.id === certificationId);
      if (cert?.cert_household) {
        cert.cert_household.composition_confirmed = true;
      }
      state.unconfirmedHouseholds = computeUnconfirmedHouseholds(
        state.certifications,
        userId
      );
    },
    /**
     * Marks a profile UserHousehold's composition as confirmed and recomputes
     * the unconfirmed list. Fired after a successful confirm POST and by the
     * `user_household_composition_confirmed` WebSocket event.
     */
    setProfileHouseholdCompositionConfirmed: (
      state,
      action: PayloadAction<{ userHouseholdId: number }>
    ) => {
      const { userHouseholdId } = action.payload;
      state.userHouseholds = state.userHouseholds.map((uh) =>
        uh.user_household_id === userHouseholdId
          ? { ...uh, composition_confirmed: true }
          : uh
      );
      state.unconfirmedProfileHouseholds = computeUnconfirmedProfileHouseholds(
        state.userHouseholds
      );
    },
    addHouseholdConfirmed: (state, action: PayloadAction<number>) => {
      if (!state.householdsConfirmed.includes(action.payload)) {
        state.householdsConfirmed.push(action.payload);
      }
    },
    removeHouseholdConfirmed: (state, action: PayloadAction<number>) => {
      state.householdsConfirmed = state.householdsConfirmed.filter(
        (id) => id !== action.payload
      );
    },
    addHouseholdTransferred: (
      state,
      action: PayloadAction<TransferredEventData>
    ) => {
      state.householdsTransferred.push(action.payload);
    },
    removeHouseholdTransferred: (state, action: PayloadAction<number>) => {
      state.householdsTransferred = state.householdsTransferred.filter(
        (t) => t.certification_id !== action.payload
      );
    },
    updateHouseholdName: (
      state,
      action: PayloadAction<UpdateHouseholdNamePayload>
    ) => {
      const { certificationId, name } = action.payload;
      const cert = state.certifications.find((c) => c.id === certificationId);
      if (cert?.cert_household) {
        cert.cert_household.name = name;
      }
    },
    patchCertificationsSignatures: (
      state,
      action: PayloadAction<FormSignatureRequestType[]>
    ) => {
      const byCertId = action.payload.reduce<
        Record<number, FormSignatureRequestType[]>
      >((acc, fsr) => {
        const certId = fsr.certification;
        if (!acc[certId]) {
          acc[certId] = [];
        }
        acc[certId].push(fsr);
        return acc;
      }, {});

      state.certifications = state.certifications.map((cert) => ({
        ...cert,
        form_signatures_request:
          byCertId[cert.id] ?? cert.form_signatures_request ?? [],
      }));
      state.fsrsLoaded = true;
    },
    markSignatureCompleted: (
      state,
      action: PayloadAction<{ isrId: number }>
    ) => {
      const { isrId } = action.payload;
      for (const cert of state.certifications) {
        for (const fsr of cert.form_signatures_request ?? []) {
          const sig = (fsr.needed_signatures ?? []).find((s) => s.id === isrId);
          if (sig) {
            sig.signature_completed = true;
            return;
          }
        }
      }
    },
    removeFormSignatureRequestByDocId: (
      state,
      action: PayloadAction<{ certDocId: number }>
    ) => {
      const { certDocId } = action.payload;
      for (const cert of state.certifications) {
        const fsrs = cert.form_signatures_request ?? [];
        const idx = fsrs.findIndex(
          (fsr) => fsr.certification_document === certDocId
        );
        if (idx !== -1) {
          fsrs.splice(idx, 1);
          return;
        }
      }
    },
    upsertFormSignatureRequest: (
      state,
      action: PayloadAction<{
        certificationId: number;
        certDocId: number;
        fsr: {
          id: number;
          requested?: boolean;
          request_date?: string | null;
          document_title?: string | null;
          signed_form?: string | null;
          generated_form_url?: string | null;
          needed_signatures: Array<{
            id: number;
            signature_completed: boolean;
            compliance: boolean;
            household_member: {
              id: number;
              user: number;
              first_name: string;
              last_name: string;
            } | null;
            x: number;
            y: number;
            width: number;
            height: number;
            certification_page: { id: number; order: number } | null;
            sit_name: string | null;
          }>;
        };
      }>
    ) => {
      const { certificationId, certDocId, fsr: wsFsr } = action.payload;
      const cert = state.certifications.find((c) => c.id === certificationId);
      if (!cert) {
        return;
      }
      if (!cert.form_signatures_request) {
        cert.form_signatures_request = [];
      }

      const existingIdx = cert.form_signatures_request.findIndex(
        (f) => f.id === wsFsr.id || f.certification_document === certDocId
      );
      const existing =
        existingIdx !== -1 ? cert.form_signatures_request[existingIdx] : null;

      const mapped: FormSignatureRequestType = {
        id: wsFsr.id,
        certification: certificationId,
        certification_document: certDocId,
        document_title:
          wsFsr.document_title ?? existing?.document_title ?? undefined,
        requested: wsFsr.requested ?? existing?.requested ?? false,
        auto_requested: existing?.auto_requested ?? false,
        request_date: wsFsr.request_date ?? existing?.request_date ?? null,
        signed_form: wsFsr.signed_form ?? existing?.signed_form ?? null,
        form: existing?.form ?? 0,
        generated_form:
          existing?.generated_form ??
          ('' as FormSignatureRequestType['generated_form']),
        generated_form_url:
          wsFsr.generated_form_url ??
          existing?.generated_form_url ??
          ('' as FormSignatureRequestType['generated_form_url']),
        is_reachable: true,
        needed_signatures: wsFsr.needed_signatures.map((sig) => ({
          id: sig.id,
          signature_completed: sig.signature_completed,
          compliance: sig.compliance,
          user_id: (sig as Record<string, unknown>).user_id as
            | number
            | undefined,
          household_member: sig.household_member as HouseholdMember | null,
          is_date: false,
          is_name: false,
          is_hhmt: false,
          is_phone: false,
          x: sig.x,
          y: sig.y,
          width: sig.width,
          height: sig.height,
          certification_page: sig.certification_page,
          sit_name: sig.sit_name,
          is_reachable: true,
          sign_date: null,
          sign_hash: null,
          remote_address: null,
          user_agent: null,
          text: null,
          is_TIC_signature: false,
          x_hash: null,
          y_hash: null,
          form_signature_request: {} as FormSignatureRequestType,
          certification: certificationId,
          form: 0,
          form_page: 0,
          compliance_user: null,
          signature_reference: false,
          xfdf_string: null,
        })),
      };

      if (existingIdx !== -1) {
        cert.form_signatures_request[existingIdx] = mapped;
      } else {
        cert.form_signatures_request.push(mapped);
      }
    },

    // --- Schema & Answers (Questionnaire) ---

    setSchema: (
      state,
      action: PayloadAction<{ configId: number; schema: QuestionnaireSchema }>
    ) => {
      state.schemas[action.payload.configId] = action.payload.schema;
      state.schemasLoading[action.payload.configId] = false;
    },
    setSchemaLoading: (
      state,
      action: PayloadAction<{ configId: number; loading: boolean }>
    ) => {
      state.schemasLoading[action.payload.configId] = action.payload.loading;
    },
    setSchemaFailed: (
      state,
      action: PayloadAction<{ configId: number; failed: boolean }>
    ) => {
      state.schemasFailed[action.payload.configId] = action.payload.failed;
    },
    setAnswers: (
      state,
      action: PayloadAction<{ certId: number; data: QuestionnaireAnswersMap }>
    ) => {
      state.answers[action.payload.certId] = action.payload.data;
      state.answersLoaded[action.payload.certId] = true;
      state.answersLoading[action.payload.certId] = false;
    },
    setAnswersLoaded: (
      state,
      action: PayloadAction<{ certId: number; loaded: boolean }>
    ) => {
      state.answersLoaded[action.payload.certId] = action.payload.loaded;
    },
    setAnswersLoading: (
      state,
      action: PayloadAction<{ certId: number; loading: boolean }>
    ) => {
      state.answersLoading[action.payload.certId] = action.payload.loading;
    },
    setAnswersFailed: (
      state,
      action: PayloadAction<{ certId: number; failed: boolean }>
    ) => {
      state.answersFailed[action.payload.certId] = action.payload.failed;
    },
    setQuestionnaireVisibility: (
      state,
      action: PayloadAction<{
        certId: number;
        visibleGroupIds: number[];
        visibleQitIds: number[];
        newlyRevealed: number[];
        revealOrigin: Record<
          number,
          { questionName: string; choiceName: string }
        >;
        filteredQitIds: number[];
        memberTypeId: number | null;
      }>
    ) => {
      const { certId, ...rest } = action.payload;
      state.questionnaireVisibility[certId] = rest;
    },

    // WebSocket-driven reducers
    upsertAnswer: (state, action: PayloadAction<UpsertAnswerPayload>) => {
      const { certificationId, answer } = action.payload;
      const certAnswers = state.answers[certificationId];
      if (!certAnswers) {
        return;
      }
      const { qit_id, answer_group_id, survey_id } = answer;
      if (survey_id == null) {
        return;
      }
      if (!certAnswers[qit_id]) {
        certAnswers[qit_id] = {};
      }
      if (!certAnswers[qit_id][survey_id]) {
        certAnswers[qit_id][survey_id] = {};
      }
      certAnswers[qit_id][survey_id][answer_group_id] = answer;
    },
    removeAnswer: (state, action: PayloadAction<RemoveAnswerPayload>) => {
      const { certificationId, qitId, surveyId, answerGroupId } =
        action.payload;
      const certAnswers = state.answers[certificationId];
      if (!certAnswers?.[qitId]?.[surveyId]) {
        return;
      }
      delete certAnswers[qitId][surveyId][answerGroupId];
      if (Object.keys(certAnswers[qitId][surveyId]).length === 0) {
        delete certAnswers[qitId][surveyId];
      }
      if (Object.keys(certAnswers[qitId]).length === 0) {
        delete certAnswers[qitId];
      }
    },
    addFileToAnswer: (state, action: PayloadAction<AddFileToAnswerPayload>) => {
      const { certificationId, qitId, surveyId, answerGroupId, file } =
        action.payload;
      const answer =
        state.answers[certificationId]?.[qitId]?.[surveyId]?.[answerGroupId];
      if (!answer) {
        return;
      }
      if (!answer.files) {
        answer.files = [];
      }
      const existingIdx = answer.files.findIndex((f) => f.id === file.id);
      if (existingIdx !== -1) {
        answer.files[existingIdx] = file;
      } else {
        answer.files.push(file);
      }
    },
    removeFileFromAnswer: (
      state,
      action: PayloadAction<RemoveFileFromAnswerPayload>
    ) => {
      const { certificationId, qitId, surveyId, answerGroupId, fileId } =
        action.payload;
      const answer =
        state.answers[certificationId]?.[qitId]?.[surveyId]?.[answerGroupId];
      if (!answer?.files) {
        return;
      }
      answer.files = answer.files.filter((f) => f.id !== fileId);
    },
    addSurveyToCertification: (
      state,
      action: PayloadAction<SurveyEventPayload>
    ) => {
      const { certificationId, survey } = action.payload;
      const cert = state.certifications.find((c) => c.id === certificationId);
      if (!cert?.cert_household || !survey.cert_member_id) {
        return;
      }
      const member = cert.cert_household.cert_members.find(
        (m) => m.cert_member_id === survey.cert_member_id
      );
      if (!member) {
        return;
      }
      if (!member.surveys.includes(survey.id)) {
        member.surveys.push(survey.id);
      }
    },
    removeSurveyFromCertification: (
      state,
      action: PayloadAction<{ certificationId: number; surveyId: number }>
    ) => {
      const { certificationId, surveyId } = action.payload;
      const cert = state.certifications.find((c) => c.id === certificationId);
      if (!cert?.cert_household) {
        return;
      }
      for (const member of cert.cert_household.cert_members) {
        member.surveys = member.surveys.filter((id) => id !== surveyId);
      }
    },
    adjustSignatureCount: (
      state,
      action: PayloadAction<{
        certId: number;
        cmId: number;
        delta: 1 | -1;
      }>
    ) => {
      const { certId, cmId, delta } = action.payload;
      const cert = state.certifications.find((c) => c.id === certId);
      if (!cert?.cert_household?.cert_members) {
        return;
      }
      const cm = cert.cert_household.cert_members.find(
        (m) => m.cert_member_id === cmId
      );
      if (cm) {
        cm.pending_signature_count = Math.max(
          0,
          (cm.pending_signature_count ?? 0) + delta
        );
      }
    },
    setRemindersSentToday: (state, action: PayloadAction<number>) => {
      const cert = state.certifications.find((c) => c.id === action.payload);
      if (cert) {
        cert.reminders_already_sent_today = true;
      }
    },
  },
});

export const {
  addHighlight,
  removeHighlight,
  clearHighlights,
  addFsrHighlight,
  removeFsrHighlight,
  setCertifications,
  setUserHouseholds,
  clearCertifications,
  setCertStatus,
  setCertStatusLoading,
  setUnconfirmedHouseholds,
  incrementHouseholdsCurrentIndex,
  setHouseholdsCurrentIndex,
  updateHouseholdMemberInCertifications,
  updateCertMemberContactsByUserId,
  setHouseholdCompositionConfirmed,
  setProfileHouseholdCompositionConfirmed,
  addHouseholdConfirmed,
  removeHouseholdConfirmed,
  addHouseholdTransferred,
  removeHouseholdTransferred,
  updateHouseholdName,
  setOpenHouseholdComposition,
  setOpenHouseholdCompositionForCertId,
  patchCertificationsSignatures,
  markSignatureCompleted,
  removeFormSignatureRequestByDocId,
  upsertFormSignatureRequest,
  setSchema,
  setSchemaLoading,
  setSchemaFailed,
  setAnswers,
  setAnswersLoaded,
  setAnswersLoading,
  setAnswersFailed,
  setQuestionnaireVisibility,
  upsertAnswer,
  removeAnswer,
  addFileToAnswer,
  removeFileFromAnswer,
  addSurveyToCertification,
  removeSurveyFromCertification,
  adjustSignatureCount,
  setRemindersSentToday,
} = NewRenterInterfaceSlice.actions;

export default NewRenterInterfaceSlice.reducer;
